import FormStore from "@/core/form/store/FormStore"
import {
  GenerateAiImageInput,
  GenerateAiImageStyle,
  GenerateImageViewMutation,
} from "@/media/generate/__generated__/GenerateImageViewMutation.graphql"
import makeUseStyles from "@assets/style/util/makeUseStyles"
import AIButton from "@components/ai/AIButton"
import AIInput from "@components/ai/AIInput"
import WithEntitlement from "@components/entitlement/WithEntitlement"
import Form from "@components/form/Form"
import { DiscoIcon, DiscoSection, DiscoSelect } from "@disco-ui"
import useHasEntitlement from "@utils/hook/useHasEntitlement"
import { observer } from "mobx-react-lite"

interface Props {
  form: FormStore<GenerateAiImageInput, GenerateImageViewMutation>
  handleSubmit: () => void
}

function GenerateImageForm(props: Props) {
  const { form, handleSubmit } = props
  const classes = useStyles()
  const hasEntitlement = useHasEntitlement("ai_image_generation")
  const isLoading = form.isSubmitting

  return (
    <Form
      id={"GenerateImageView.form"}
      onSubmit={handleSubmit}
      isSubmitAllowed={hasEntitlement}
    >
      <DiscoSection className={classes.container}>
        <div className={classes.inputWrapper}>
          <AIInput
            error={Boolean(form.errorsByField.prompt)}
            value={form.state.prompt}
            onChange={(e: any) => (form.state.prompt = e.target.value)}
            disabled={!hasEntitlement || isLoading}
            placeholder={"Describe the image you want to create"}
            className={classes.input}
            showBadge={false}
            minHeight={"150px"}
            onEnterPress={handleSubmit}
          />

          <div className={classes.controls}>
            <DiscoSelect
              testid={"GenerateImageForm.style-select"}
              autoComplete={false}
              disableClearable
              options={[
                { title: "Style: Auto", value: "auto" as GenerateAiImageStyle },
                { title: "General", value: "general" as GenerateAiImageStyle },
                { title: "Realistic", value: "realistic" as GenerateAiImageStyle },
                { title: "Design", value: "design" as GenerateAiImageStyle },
                { title: "3D Render", value: "render_3d" as GenerateAiImageStyle },
              ]}
              value={form.state.style}
              onChange={(v) => {
                if (v) form.state.style = v
              }}
              className={classes.styleSelect}
              disabled={!hasEntitlement || isLoading}
              placeholder={"Style"}
            />

            <WithEntitlement entitlement={"ai_image_generation"}>
              {({ hasEntitlement: hasEntitlementInner }) => (
                <AIButton
                  disabled={
                    !hasEntitlementInner || isLoading || !form.state.prompt?.trim()
                  }
                  onClick={handleSubmit}
                  shouldDisplaySpinner={isLoading}
                  className={classes.aiButton}
                  testid={"GenerateImageView.form.submit-button"}
                  noIcon
                >
                  <DiscoIcon
                    color={"white"}
                    active
                    icon={"iconsax.arrow-up"}
                    height={20}
                    width={20}
                  />
                </AIButton>
              )}
            </WithEntitlement>
          </div>
        </div>
      </DiscoSection>
    </Form>
  )
}

const useStyles = makeUseStyles((theme) => ({
  container: {
    background: theme.palette.background.paper,
    padding: theme.spacing(0),
    borderRadius: theme.measure.borderRadius.large,
    width: "100%",
  },
  inputWrapper: {
    position: "relative",
    width: "100%",
  },
  input: {
    width: "100%",
    "& .MuiInputBase-root": {
      borderRadius: theme.measure.borderRadius.large,
    },
    "& textarea": {
      paddingBottom: theme.spacing(6),
    },
  },
  controls: {
    position: "absolute",
    bottom: theme.spacing(1),
    right: theme.spacing(1),
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    gap: theme.spacing(1),
    zIndex: 1,
    width: `calc(100% - ${theme.spacing(2)}px)`,
  },
  styleSelect: {
    minWidth: "120px",
    background: theme.palette.background.paper,
    "& .MuiOutlinedInput-notchedOutline": {
      border: "none",
    },
    "& .MuiSelect-select": {
      backgroundColor: "transparent",
    },
  },
  aiButton: {
    height: "40px",
    width: "40px",
  },
}))

export default observer(GenerateImageForm)
